import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiChevronRight, FiHome } = FiIcons;

const Breadcrumb = ({ customItems = [] }) => {
  const location = useLocation();

  // Define breadcrumb mappings
  const breadcrumbMap = {
    '/dashboard': { name: 'Dashboard', icon: FiHome },
    '/staff': { name: 'Staff Management', icon: FiIcons.FiUsers },
    '/cases': { name: 'Employee Cases', icon: FiIcons.FiFolder },
    '/company-workflows': { name: 'Company Workflows', icon: FiIcons.FiLayers },
    '/recruitment': { name: 'Recruitment Process', icon: FiIcons.FiUsers },
    '/onboarding': { name: 'Onboarding Workflow', icon: FiIcons.FiUserCheck },
    '/core-lifecycle': { name: 'Core Employee Lifecycle', icon: FiIcons.FiSettings },
    '/performance': { name: 'Performance Management', icon: FiIcons.FiTrendingUp },
    '/employee-relations': { name: 'Employee Relations', icon: FiIcons.FiAlertTriangle },
    '/exit-processes': { name: 'Exit Processes', icon: FiIcons.FiScale },
    '/tribunal-prep': { name: 'Tribunal Preparation', icon: FiIcons.FiScale },
    '/compliance': { name: 'Compliance Tracker', icon: FiIcons.FiShield }
  };

  // Generate breadcrumb items
  const generateBreadcrumbs = () => {
    if (customItems.length > 0) {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: FiHome },
        ...customItems
      ];
    }

    const pathSegments = location.pathname.split('/').filter(segment => segment);
    
    // If we're on the dashboard, don't add it twice
    if (location.pathname === '/dashboard') {
      return [{ name: 'Dashboard', path: '/dashboard', icon: FiHome }];
    }
    
    const breadcrumbs = [{ name: 'Dashboard', path: '/dashboard', icon: FiHome }];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Skip if this is the dashboard path (already added)
      if (currentPath === '/dashboard') {
        return;
      }
      
      const breadcrumbInfo = breadcrumbMap[currentPath];

      if (breadcrumbInfo) {
        breadcrumbs.push({
          name: breadcrumbInfo.name,
          path: currentPath,
          icon: breadcrumbInfo.icon
        });
      } else {
        // Handle dynamic routes like /case/CASE-123
        if (segment.startsWith('CASE-')) {
          breadcrumbs.push({
            name: `Case ${segment}`,
            path: currentPath,
            icon: FiIcons.FiFileText
          });
        } else if (segment.startsWith('WF-')) {
          breadcrumbs.push({
            name: `Workflow ${segment}`,
            path: currentPath,
            icon: FiIcons.FiTarget
          });
        } else {
          breadcrumbs.push({
            name: segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' '),
            path: currentPath,
            icon: FiIcons.FiFolder
          });
        }
      }
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null; // Don't show breadcrumb on home page
  }

  return (
    <nav className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-3">
      <div className="max-w-7xl mx-auto">
        <ol className="flex items-center space-x-2 text-sm">
          {breadcrumbs.map((breadcrumb, index) => (
            <li key={breadcrumb.path} className="flex items-center">
              {index > 0 && (
                <SafeIcon icon={FiChevronRight} className="text-gray-400 mx-2" />
              )}
              {index === breadcrumbs.length - 1 ? (
                // Current page - not clickable
                <div className="flex items-center space-x-1 text-gray-900 font-medium">
                  <SafeIcon icon={breadcrumb.icon} className="text-gray-600" />
                  <span>{breadcrumb.name}</span>
                </div>
              ) : (
                // Previous pages - clickable
                <Link
                  to={breadcrumb.path}
                  className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <SafeIcon icon={breadcrumb.icon} />
                  <span>{breadcrumb.name}</span>
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumb;